import { useEffect, useState } from "react";
import "./WeddingHeaderStatus.css";

const WEDDING_START = new Date("2026-08-29T14:00:00+02:00").getTime();
const WEDDING_DAY_END = new Date("2026-08-30T00:00:00+02:00").getTime();
const WEATHER_URL =
  "https://api.open-meteo.com/v1/forecast?latitude=47.1747&longitude=20.1763&daily=weather_code%2Ctemperature_2m_max%2Ctemperature_2m_min%2Cprecipitation_probability_max&timezone=Europe%2FBudapest&start_date=2026-08-29&end_date=2026-08-29";

type DailyForecastResponse = {
  daily?: {
    time?: string[];
    weather_code?: number[];
    temperature_2m_max?: number[];
    temperature_2m_min?: number[];
    precipitation_probability_max?: number[];
  };
};

type WeatherStatus =
  | { state: "loading" }
  | { state: "error" }
  | {
      state: "ready";
      code: number;
      high: number;
      low: number;
      rainChance: number;
    };

function countdownText(now: number): string {
  const remaining = WEDDING_START - now;

  if (remaining <= 0) {
    return now < WEDDING_DAY_END
      ? "Ma van a nagy nap!"
      : "Már kimondtuk az igent!";
  }

  const days = Math.floor(remaining / 86_400_000);
  const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);

  return days > 0
    ? `${days} nap · ${hours} óra`
    : `${hours} óra · ${minutes} perc`;
}

function weatherPresentation(code: number): {
  icon: string;
  description: string;
} {
  if (code === 0) return { icon: "☀︎", description: "Napos" };
  if (code <= 2) return { icon: "◒", description: "Változóan felhős" };
  if (code === 3) return { icon: "☁︎", description: "Borult" };
  if (code === 45 || code === 48) return { icon: "≋", description: "Ködös" };
  if (code >= 51 && code <= 57)
    return { icon: "☂︎", description: "Szitálás" };
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82))
    return { icon: "☂", description: "Eső várható" };
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86))
    return { icon: "❄︎", description: "Havazás" };
  if (code >= 95) return { icon: "ϟ", description: "Zivataros" };
  return { icon: "◌", description: "Változékony" };
}

export default function WeddingHeaderStatus(): JSX.Element {
  const [countdown, setCountdown] = useState(() => countdownText(Date.now()));
  const [weather, setWeather] = useState<WeatherStatus>({ state: "loading" });

  useEffect(() => {
    const updateCountdown = () => setCountdown(countdownText(Date.now()));
    const interval = window.setInterval(updateCountdown, 30_000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let lastRequestedAt = 0;

    const loadWeather = async () => {
      lastRequestedAt = Date.now();

      try {
        const response = await fetch(WEATHER_URL, { cache: "no-store" });
        if (!response.ok) throw new Error("Weather request failed");

        const payload = (await response.json()) as DailyForecastResponse;
        const daily = payload.daily;

        if (
          daily?.time?.[0] !== "2026-08-29" ||
          daily.weather_code?.[0] === undefined ||
          daily.temperature_2m_max?.[0] === undefined ||
          daily.temperature_2m_min?.[0] === undefined ||
          daily.precipitation_probability_max?.[0] === undefined
        ) {
          throw new Error("Wedding-day forecast is missing");
        }

        if (isMounted) {
          setWeather({
            state: "ready",
            code: daily.weather_code[0],
            high: Math.round(daily.temperature_2m_max[0]),
            low: Math.round(daily.temperature_2m_min[0]),
            rainChance: Math.round(daily.precipitation_probability_max[0])
          });
        }
      } catch {
        if (isMounted) setWeather({ state: "error" });
      }
    };

    const refreshWhenActive = () => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastRequestedAt > 5 * 60_000
      ) {
        void loadWeather();
      }
    };

    void loadWeather();
    window.addEventListener("focus", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);

    return () => {
      isMounted = false;
      window.removeEventListener("focus", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, []);

  const weatherView =
    weather.state === "ready" ? weatherPresentation(weather.code) : null;

  return (
    <section className="footerStatus" aria-label="Esküvői visszaszámlálás és időjárás">
      <article className="footerStatus__item footerStatus__item--countdown">
        <span className="footerStatus__label">Esküvőig</span>
        <strong className="footerStatus__countdown">{countdown}</strong>
      </article>

      <article className="footerStatus__item footerStatus__item--weather" aria-live="polite">
        <span className="footerStatus__label">Augusztus 29. · Szolnok</span>
        {weather.state === "ready" && weatherView ? (
          <a
            className="footerStatus__weather"
            href="https://www.idokep.hu/idojaras/szolnok"
            target="_blank"
            rel="noreferrer"
            aria-label="Részletes szolnoki időjárás-előrejelzés megnyitása az Időképen"
            title={`Esküvőnapi előrejelzés: ${weatherView.description}. Részletek az Időképen; a kijelzett adatok forrása: Open-Meteo.`}
          >
            <span className="footerStatus__weatherIcon" aria-hidden="true">
              {weatherView.icon}
            </span>
            <strong>{weather.high}°</strong>
            <small>
              {weather.low}° · {weather.rainChance}% eső
            </small>
          </a>
        ) : (
          <span className="footerStatus__weatherState">
            {weather.state === "loading" ? "Frissül…" : "Nem elérhető"}
          </span>
        )}
      </article>
    </section>
  );
}
